import { ArrowLeftIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import LoyaltyProgramHistory from "../components/LoyaltyProgramHistory";
import Toast from "../components/Toast";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import {
  copyProgram,
  createProgram,
  examplePoints,
  fetchBranchProgram,
  fetchPrograms,
  updateProgram,
} from "../services/loyaltyService";
import { can } from "../services/permissions";
import { fetchCatalogue } from "../services/serviceService";
import { labelFor } from "../services/enumLabels";

const EMPTY = {
  branch_id: "",
  is_active: false,
  mode: "points",
  earn_on: "completion",
  points_per_currency: "1",
  points_per_visit: "0",
  service_points: [],
  stamps_per_visit: "1",
  stamps_required: "10",
  first_visit_bonus: "0",
  points_expire_after_months: "",
  reward_validity_days: "",
};

/** API programme -> form (numbers as strings for the inputs). */
const toForm = (program) =>
  program
    ? {
        branch_id: program.branch_id ?? "",
        is_active: Boolean(program.is_active),
        mode: program.mode,
        earn_on: program.earn_on,
        points_per_currency: String(program.points_per_currency ?? "0"),
        points_per_visit: String(program.points_per_visit ?? 0),
        service_points: (program.service_points ?? []).map((row) => ({
          service_id: row.service_id,
          points: String(row.points),
        })),
        stamps_per_visit: String(program.stamps_per_visit ?? 1),
        stamps_required: String(program.stamps_required ?? 10),
        first_visit_bonus: String(program.first_visit_bonus ?? 0),
        points_expire_after_months: program.points_expire_after_months ?? "",
        reward_validity_days: program.reward_validity_days ?? "",
      }
    : EMPTY;

const int = (value) => (value === "" || value == null ? 0 : Number(value));
const intOrNull = (value) => (value === "" || value == null ? null : Number(value));

/** Form -> API body (every setting; birthday / referral are not sent — nothing awards them yet). */
const toBody = (form) => ({
  branch_id: form.branch_id || null,
  is_active: form.is_active,
  mode: form.mode,
  earn_on: form.earn_on,
  points_per_currency: form.points_per_currency === "" ? 0 : form.points_per_currency,
  points_per_visit: int(form.points_per_visit),
  service_points: form.service_points
    .filter((row) => row.service_id)
    .map((row) => ({ service_id: row.service_id, points: int(row.points) })),
  stamps_per_visit: int(form.stamps_per_visit),
  stamps_required: int(form.stamps_required),
  first_visit_bonus: int(form.first_visit_bonus),
  points_expire_after_months: intOrNull(form.points_expire_after_months),
  reward_validity_days: intOrNull(form.reward_validity_days),
});

/**
 * Loyalty → Programs → one branch's programme.
 *
 * - `/loyalty/programs/new` — a new programme; a branch must be chosen (those
 *   that already have one are disabled). `?copy={id}` pre-fills it from that
 *   programme and saves through the copy endpoint, so its version 1 is noted
 *   "Copied from …"; the source is untouched.
 * - `/loyalty/programs/:programId` — read-only view (`loyalty.view`), with
 *   the History tab; `/edit` makes it editable (`loyalty.manage`). Moving it
 *   to another branch is allowed only to a branch without a programme.
 *
 * Every save that changes a setting becomes a new read-only version; changing
 * the rules never touches balances already earned. The "Spend 100 → earn N"
 * line is an illustration in whole numbers (floored, like the server).
 */
export default function LoyaltyProgram({ mode = "view" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { programId } = useParams();
  const [params, setParams] = useSearchParams();
  const copyFrom = mode === "new" ? params.get("copy") : null;
  const presetBranch = mode === "new" ? params.get("branch") : null;
  const mayManage = can(getCurrentUser(), "loyalty.manage");
  const readOnly = mode === "view" || !mayManage;

  const [form, setForm] = useState(EMPTY);
  const [program, setProgram] = useState(null);
  const [source, setSource] = useState(null);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const tab = mode !== "new" && params.get("tab") === "history" ? "history" : "settings";
  const [changeNote, setChangeNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const sourceId = mode === "new" ? copyFrom : programId;
    Promise.all([
      fetchPrograms(),
      sourceId ? fetchBranchProgram(sourceId) : Promise.resolve(null),
      fetchCatalogue({ perPage: 100 }),
    ])
      .then(([list, one, catalogue]) => {
        if (cancelled) return;
        setBranches(list.branches);
        setServices(catalogue.services ?? []);
        if (mode === "new") {
          setSource(one?.program ?? null);
          // A copy keeps every setting but the branch, which must be chosen.
          setForm({ ...toForm(one?.program ?? null), branch_id: presetBranch ?? "" });
        } else {
          setProgram(one.program);
          setForm(toForm(one.program));
        }
      })
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t("loyalty.loadFailed"))))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [mode, programId, copyFrom, presetBranch, t]);

  const set = (name) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const branchNames = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);
  const awardsPoints = form.mode !== "stamps";
  const awardsStamps = form.mode !== "points";
  const example = useMemo(() => examplePoints(100, form.points_per_currency), [form.points_per_currency]);
  const serviceName = (id) => services.find((s) => s.id === id)?.name ?? "";
  const unusedServices = (current) =>
    services.filter((s) => s.id === current || !form.service_points.some((row) => row.service_id === s.id));

  const setRow = (index, changes) =>
    setForm((f) => ({
      ...f,
      service_points: f.service_points.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    }));

  const setTab = (name) => {
    const next = new URLSearchParams(params);
    if (name === "history") next.set("tab", "history");
    else next.delete("tab");
    setParams(next, { replace: true });
  };

  // Saving asks first: the rules only reach future visits.
  const submit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    setConfirming(true);
  };

  const save = async () => {
    setConfirming(false);
    setSaving(true);
    setError("");
    setFieldErrors({});
    const body = { ...toBody(form), change_note: changeNote.trim() || null };
    try {
      if (mode === "new" && source) {
        const { programs } = await copyProgram(source.id, body);
        navigate(`/loyalty/programs/${programs[0].id}`, { replace: true });
        return;
      }
      if (mode === "new") {
        const created = await createProgram(body);
        navigate(`/loyalty/programs/${created.program.id}`, { replace: true });
        return;
      }
      const saved = await updateProgram(program.id, body);
      setProgram(saved.program);
      setForm(toForm(saved.program));
      setChangeNote("");
      setHistoryKey((k) => k + 1);
      setToast({
        type: "success",
        message: saved.versionCreated
          ? t("loyalty.program.savedVersion", { number: saved.version?.version_number })
          : t("loyalty.program.unchanged"),
      });
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("loyalty.program.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const err = (name) => fieldErrors[name]?.[0];

  const numberField = (name, label, { hint, step = "1", min = "0" } = {}) => (
    <div>
      <label className="field-label" htmlFor={`lp-${name}`}>
        {label}
      </label>
      <input
        id={`lp-${name}`}
        type="number"
        inputMode={step === "1" ? "numeric" : "decimal"}
        min={min}
        step={step}
        dir="ltr"
        className={`field-input ${err(name) ? "border-danger" : ""}`}
        value={form[name]}
        onChange={set(name)}
      />
      {hint && <p className="mt-1 text-xs text-ink-2">{hint}</p>}
      {err(name) && <p className="field-error">{err(name)}</p>}
    </div>
  );

  if (loading) return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;

  if (mode !== "new" && !program) {
    return (
      <div className="space-y-3">
        <Link to="/loyalty/programs" className="inline-flex items-center gap-1 text-sm text-accent-text">
          <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
          {t("loyalty.programs.back")}
        </Link>
        <p
          className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error || t("loyalty.loadFailed")}
        </p>
      </div>
    );
  }

  const title =
    mode === "new"
      ? source
        ? t("loyalty.programs.copyTitle", { branch: source.branch?.name ?? "" })
        : t("loyalty.programs.newTitle")
      : t("loyalty.programs.programFor", { branch: program.branch?.name ?? "" });

  // The programme's own branch, and any branch without a programme, can be chosen.
  const branchFree = (branch) => !branch.program_id || branch.program_id === program?.id;
  const saveButton = !readOnly && tab === "settings" && (
    <button type="submit" className="btn-primary" disabled={saving}>
      {saving ? t("common.saving") : mode === "new" ? t("loyalty.programs.create") : t("common.save")}
    </button>
  );

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <Link to="/loyalty/programs" className="inline-flex items-center gap-1 text-sm text-accent-text">
        <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
        {t("loyalty.programs.back")}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{title}</h1>
          <p className="text-sm text-ink-2">
            {source ? t("loyalty.programs.copyHint") : t("loyalty.program.subtitle")}
          </p>
          {program?.current_version && (
            <p className="mt-1 text-xs text-ink-2">
              {t("loyalty.history.version", { number: program.current_version.version_number })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {mode === "view" && mayManage && (
            <Link
              to={`/loyalty/programs/${program.id}/edit`}
              className="btn-secondary inline-flex items-center gap-1"
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("common.edit")}
            </Link>
          )}
          {saveButton}
        </div>
      </div>

      {/* Tabs */}
      {mode !== "new" && (
        <div className="flex gap-1 overflow-x-auto border-b border-line" role="tablist">
          {["settings", "history"].map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              onClick={() => setTab(name)}
              className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
                tab === name
                  ? "border-accent text-accent-text"
                  : "border-transparent text-ink-2 hover:text-ink"
              }`}
            >
              {t(`loyalty.history.tabs.${name}`)}
            </button>
          ))}
        </div>
      )}

      {tab === "history" && (
        <LoyaltyProgramHistory programId={program.id} branchNames={branchNames} refreshKey={historyKey} />
      )}

      {tab === "settings" && error && (
        <div
          className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error}
        </div>
      )}

      {tab === "settings" && (
        <fieldset disabled={readOnly} className="space-y-4">
          {/* Branch */}
          <section className="card space-y-2 p-5">
            <label className="field-label" htmlFor="lp-branch">
              {t("loyalty.programs.branch")}
            </label>
            <select
              id="lp-branch"
              className={`field-input ${err("branch_id") ? "border-danger" : ""}`}
              value={form.branch_id}
              onChange={set("branch_id")}
              required
            >
              <option value="">{t("loyalty.programs.chooseBranch")}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id} disabled={!branchFree(branch)}>
                  {branchFree(branch)
                    ? branch.name
                    : t("loyalty.programs.branchTaken", { branch: branch.name })}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink-2">
              {mode === "new" ? t("loyalty.programs.branchHintNew") : t("loyalty.programs.branchHintEdit")}
            </p>
            {err("branch_id") && <p className="field-error">{err("branch_id")}</p>}
          </section>

          {/* On / off and what it awards */}
          <section className="card space-y-4 p-5">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                className="h-5 w-5 accent-[var(--pl-accent)]"
                checked={form.is_active}
                onChange={set("is_active")}
              />
              <span>
                <span className="block font-semibold">{t("loyalty.program.active")}</span>
                <span className="block text-sm text-ink-2">
                  {mode === "new" ? t("loyalty.program.firstSaveHint") : t("loyalty.programs.activeHint")}
                </span>
              </span>
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="lp-mode">
                  {t("loyalty.program.mode")}
                </label>
                <select id="lp-mode" className="field-input" value={form.mode} onChange={set("mode")}>
                  {["points", "stamps", "both"].map((value) => (
                    <option key={value} value={value}>
                      {labelFor("loyalty_mode", value)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="lp-earn">
                  {t("loyalty.program.earnOn")}
                </label>
                <select id="lp-earn" className="field-input" value={form.earn_on} onChange={set("earn_on")}>
                  {["completion", "payment"].map((value) => (
                    <option key={value} value={value}>
                      {labelFor("loyalty_earn_on", value)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-2">{t(`loyalty.earnOnHint.${form.earn_on}`)}</p>
              </div>
            </div>
          </section>

          {/* Points */}
          {awardsPoints && (
            <section className="card space-y-4 p-5">
              <h2 className="font-semibold">{t("loyalty.program.points")}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {numberField("points_per_currency", t("loyalty.program.perCurrency"), { step: "0.01" })}
                {numberField("points_per_visit", t("loyalty.program.perVisit"))}
              </div>
              <p className="rounded-lg bg-accent-wash px-3 py-2 text-sm text-accent-text" aria-live="polite">
                {t("loyalty.program.example", { spend: 100, points: example })}
              </p>

              {/* Per-service points */}
              <div>
                <h3 className="text-sm font-semibold">{t("loyalty.program.servicePoints")}</h3>
                <p className="mb-2 text-xs text-ink-2">{t("loyalty.program.servicePointsHint")}</p>
                {form.service_points.length > 0 && (
                  <ul className="mb-2 space-y-2">
                    {form.service_points.map((row, index) => (
                      <li key={index} className="flex flex-wrap items-center gap-2">
                        <select
                          aria-label={t("loyalty.program.service")}
                          className="field-input min-w-[200px] flex-1"
                          value={row.service_id}
                          onChange={(e) => setRow(index, { service_id: e.target.value })}
                        >
                          <option value="">{t("loyalty.program.chooseService")}</option>
                          {unusedServices(row.service_id).map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name}
                            </option>
                          ))}
                          {row.service_id && !services.some((s) => s.id === row.service_id) && (
                            <option value={row.service_id}>
                              {serviceName(row.service_id) || row.service_id}
                            </option>
                          )}
                        </select>
                        <input
                          type="number"
                          min="0"
                          dir="ltr"
                          aria-label={t("loyalty.program.pointsLabel")}
                          className="field-input w-28"
                          value={row.points}
                          onChange={(e) => setRow(index, { points: e.target.value })}
                        />
                        <button
                          type="button"
                          className="btn-secondary px-2"
                          aria-label={t("common.remove")}
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              service_points: f.service_points.filter((_, i) => i !== index),
                            }))
                          }
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {Object.keys(fieldErrors).some((key) => key.startsWith("service_points")) && (
                  <p className="field-error">
                    {Object.entries(fieldErrors).find(([key]) => key.startsWith("service_points"))[1][0]}
                  </p>
                )}
                <button
                  type="button"
                  className="btn-secondary inline-flex items-center gap-1"
                  disabled={unusedServices("").length === 0}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      service_points: [...f.service_points, { service_id: "", points: "10" }],
                    }))
                  }
                >
                  <PlusIcon className="h-4 w-4" aria-hidden="true" />
                  {t("loyalty.program.addServicePoints")}
                </button>
              </div>
            </section>
          )}

          {/* Stamps */}
          {awardsStamps && (
            <section className="card space-y-4 p-5">
              <h2 className="font-semibold">{t("loyalty.program.stamps")}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {numberField("stamps_per_visit", t("loyalty.program.stampsPerVisit"))}
                {numberField("stamps_required", t("loyalty.program.stampsRequired"), {
                  min: "1",
                  hint: t("loyalty.program.stampsRequiredHint", { count: int(form.stamps_required) }),
                })}
              </div>
            </section>
          )}

          {/* Bonuses and expiry */}
          <section className="card space-y-4 p-5">
            <h2 className="font-semibold">{t("loyalty.program.bonuses")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {numberField("first_visit_bonus", t("loyalty.program.firstVisit"))}
            </div>
            <p className="text-xs text-ink-2">{t("loyalty.program.bonusesHint")}</p>

            <h2 className="pt-2 font-semibold">{t("loyalty.program.expiry")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {numberField("points_expire_after_months", t("loyalty.program.expiryMonths"), {
                min: "1",
                hint: t("loyalty.program.expiryMonthsHint"),
              })}
              {numberField("reward_validity_days", t("loyalty.program.rewardValidity"), {
                min: "1",
                hint: t("loyalty.program.rewardValidityHint"),
              })}
            </div>
          </section>

          {/* Why the rules changed — kept on the new version. */}
          {!readOnly && (
            <div className="flex flex-wrap items-end justify-end gap-3">
              <div className="min-w-[240px] flex-1 sm:max-w-md">
                <label className="field-label" htmlFor="lp-change-note">
                  {t("loyalty.history.changeNote")}
                </label>
                <input
                  id="lp-change-note"
                  type="text"
                  maxLength={500}
                  className={`field-input ${err("change_note") ? "border-danger" : ""}`}
                  placeholder={t("loyalty.history.changeNotePlaceholder")}
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                />
                {err("change_note") && <p className="field-error">{err("change_note")}</p>}
              </div>
              {saveButton}
            </div>
          )}
        </fieldset>
      )}

      <ConfirmDialog
        open={confirming}
        title={mode === "new" ? t("loyalty.programs.confirmCreateTitle") : t("loyalty.history.confirmTitle")}
        message={mode === "new" ? t("loyalty.programs.confirmCreateBody") : t("loyalty.history.confirmBody")}
        confirmLabel={mode === "new" ? t("loyalty.programs.create") : t("common.save")}
        tone="primary"
        busy={saving}
        onConfirm={save}
        onClose={() => setConfirming(false)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </form>
  );
}
